import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTransaction } from '@elastic/apm-rum-react';
import { Grid, Typography, Box, Slide, useTheme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import useOrganization from '../../hooks/UseOrganization';
import {
  CommonTableCell,
  DateTimeCell,
} from '../../components/Common/CommonTableCell';
import utils from '../../utils/Utils';

/**
 * @component CertificationSetting
 * @description CertificationSetting - Renders a component for managing ApiKeys details.
 * @returns {JSX.Element} CertificationSetting component.
 */
const CertificationSetting = () => {
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();

  const tableColumns = useMemo(
    () => [
      {
        id: 'Product',
        heading: 'Product',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: (row) => row.Product.name,
        Cell: CommonTableCell,
        width: 100,
        sortType: utils.caseInsensitiveSort,
      },
      {
        heading: 'Created On',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'created_at',
        Cell: DateTimeCell,
        width: 150,
      },
      {
        heading: 'Last Modified',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'updated_at',
        Cell: DateTimeCell,
        width: 150,
      },
    ],
    []
  );
  const { getCertificateList, postCertificateRefresh } = useOrganization();
  const [certificateList, setCertificateList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const menus = useMemo(
    () => [
      {
        label: 'Refresh',
        id: 'refresh',
        icon: RefreshIcon,
        onClick: ({ props }) => {
          const productId = props?.original?.Product?.Id;
          postCertificateRefresh(
            {
              product_id: productId,
            },
            orgName
          ).then(() => {
            getCertificateList(orgName).then((resp) => {
              setCertificateList(resp?.data);
            });
          });
        },
      },
    ],
    []
  );

  useEffect(() => {
    setIsLoading(true);
    getCertificateList(orgName).then((resp) => {
      setCertificateList(resp?.data);
      setIsLoading(false);
    });
  }, [orgName]);

  return (
    <Grid item xs={12}>
      <Box margin="20px" display="flex" flexDirection="column" overflow="auto">
        <Typography variant="h3">Certificate</Typography>
        <Grid>
          {isLoading ? (
            <Box
              flexGrow={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              height="20vh"
            >
              <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Slide in={true} direction="up" mountOnEnter timeout={500}>
              {certificateList?.length > 0 ? (
                <Box pt={2}>
                  <ReactMuiTableListView
                    data={certificateList}
                    columns={tableColumns}
                    getHeaderProps={() => ({
                      style: {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    })}
                    getRowProps={() => ({
                      style: {
                        position: 'relative',
                      },
                    })}
                    enableRowSelection={true}
                    rowLevelRightClickMenus={menus}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  overflow="auto"
                  minHeight="300px"
                  height="calc(100vh - 438px)"
                >
                  <NoDataCreated
                    style={{ color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="subtitle1"
                    textAlign="center"
                    sx={{ pt: '10px' }}
                  >
                    {' '}
                    No certificate created yet.
                  </Typography>
                </Box>
              )}
            </Slide>
          )}
        </Grid>
      </Box>
    </Grid>
  );
};

CertificationSetting.propTypes = {
  original: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default withTransaction(
  'CertificationSetting',
  'component'
)(CertificationSetting);
